import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, Search } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { QuoteTypeBadge } from "@/components/shared/QuoteTypeBadge";
import { api } from "@/lib/api";
import { BRAND_PHOTOS, CATEGORIES, CATEGORY_PHOTO, CATEGORY_TITLE } from "@/lib/constants";

export const ServiceCard = ({ s }) => (
  <article data-testid={`service-card-${s.slug}`} className="group flex flex-col rounded-2xl bg-white border border-black/5 p-6 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#020165]">{CATEGORY_TITLE[s.category]}</span>
      <QuoteTypeBadge type={s.pricing?.type} testId={`service-card-type-${s.slug}`} />
    </div>
    <h3 className="mt-3 font-display font-bold text-xl text-[#0D141A]">{s.title}</h3>
    <p className="mt-2 text-sm text-[#56585E] leading-relaxed">{s.summary}</p>
    <ul className="mt-4 space-y-1.5 text-sm text-[#1D1E20]">
      {(s.inclusions || []).slice(0, 3).map((x) => <li key={x} className="flex gap-2"><span className="text-[#F86900]">—</span>{x}</li>)}
    </ul>
    <div className="mt-auto pt-5 flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-xs text-[#56585E]"><Clock className="h-3.5 w-3.5" />{s.duration_label}</span>
      <div className="flex gap-2">
        <Link to={`/services/${s.slug}`} data-testid={`service-card-view-${s.slug}`} className="text-sm font-semibold px-3 py-1.5 rounded-full border border-[#020165]/20 hover:bg-[#020165]/5 transition-colors">Details</Link>
        <Link to={`/quote?service=${s.slug}`} data-testid={`service-card-quote-${s.slug}`} className="text-sm font-semibold px-3 py-1.5 rounded-full bg-[#020165] text-white hover:bg-[#0a0a8a] transition-colors">Quote</Link>
      </div>
    </div>
  </article>
);

export default function Services({ fixedCategory }) {
  const params = useParams();
  const initial = fixedCategory || params.category || "all";
  const [services, setServices] = useState([]);
  const [cat, setCat] = useState(initial);
  const [q, setQ] = useState("");

  useEffect(() => { setCat(initial); }, [initial]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data.services)); }, []);

  const shown = useMemo(() => services.filter((s) =>
    (cat === "all" || s.category === cat) &&
    (!q || `${s.title} ${s.summary}`.toLowerCase().includes(q.toLowerCase()))), [services, cat, q]);
  const catInfo = CATEGORIES.find((c) => c.key === cat);

  return (
    <PageShell testId="services-page" overline="Service catalogue" image={catInfo ? CATEGORY_PHOTO[cat] : BRAND_PHOTOS[4]}
      title={catInfo ? catInfo.title : "Every service, priced transparently."}
      intro={catInfo ? catInfo.desc : "Each service shows how it is priced: an instant price, an indicative range, or an assessment before we commit to a number."}>
      <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {[["all", "All"], ...CATEGORIES.map((c) => [c.key, c.title])].map(([k, l]) => (
            <button key={k} onClick={() => setCat(k)} data-testid={`services-filter-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cat === k ? "bg-[#020165] text-white" : "bg-white text-[#1D1E20] hover:bg-black/5"}`}>{l}</button>
          ))}
        </div>
        <label className="relative md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#56585E]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services" data-testid="services-search-input"
            className="w-full rounded-full border bg-white pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#F86900]" />
        </label>
      </div>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
        {shown.map((s) => <ServiceCard key={s.slug} s={s} />)}
      </div>
      {!shown.length && services.length > 0 && <p className="mt-10 text-[#56585E]" data-testid="services-empty">No services match your search.</p>}
    </PageShell>
  );
}
